// import groupBy from 'lodash/groupBy';

// import { selectAccounts } from '@portal/store/src/account/selectors';
//TODO: uncomment rest of the selectors when account store is migrated
import difference from 'lodash/difference';
import memoize from 'lodash/memoize';
import { createSelector } from 'reselect';

import type { UserDeactivation } from '@evenfinancial/auth-client';
import type { AccountOwners } from '@evenfinancial/finance-client';
import { OnboardingContractType } from '@evenfinancial/finance-client';

import type {
  PortalSubAccount,
  Roles,
  TempAccount,
  UserGrantsData,
  UserWithRoles,
} from '@portal/api-models';
import {
  getUserAccessRoles,
  getUserPartnerRoles,
  getUserTypeRole,
} from '@portal/common/dist/access-control';

import { selectAccountsKeyedByUuid } from '../account/selectors';
import { selectAllSubAccountsKeyedByUuid } from '../subaccount/selectors';
import type { ApplicationState } from '../types';

import type { UserState } from './types';
import { getUserFullName } from './util';

export enum UserAccountsStatus {
  All = 'All',
  Empty = 'n/a',
}

export interface UserAccountLink {
  name?: string;
  uuid: string;
}

export interface UserRow extends UserWithRoles {
  accessRoles?: string[];
  accountName?: string;
  accounts?: UserAccountLink[];
  accountsStatus?: UserAccountsStatus;
  completeName?: string;
  deactivationId?: number;
  isActive?: boolean;
  isActiveDisplay?: string;
  partnerRoles?: string[];
  userType?: string;
}

export interface UserWithDeactivationInfo extends UserWithRoles {
  isActive: boolean;
}

export interface UserAvatarProps {
  accessTokenId?: number;
  accessTokenUuid?: string;
  accountUuid?: string;
  avatarUrl?: string;
  className?: string;
  name?: string;
  size?: number | 'small' | 'default' | 'large';
  userRows?: UserWithRoles[];
  userUuid?: string;
  withTooltip?: boolean;
}

export type GetUserByUuid = (
  uuid: string | undefined
) => UserWithRoles | undefined;

export type GetUserFullNameByUuid = (uuid: string | undefined) => string;

export const selectUserState = (state: ApplicationState) => state.user;

export const selectUsers = (state: ApplicationState) => state.user.rows;

export const selectUsersAccess = (state: ApplicationState) =>
  state.user.accessByUuid;

export const selectUsersByUuid = (state: ApplicationState) =>
  state.user.rowsByUuid;

export const selectAllUsersByUuid = createSelector([selectUsers], (users) =>
  users.reduce((accum, user) => {
    accum[user.accountUuid] = user;
    return accum;
  }, {} as Record<string, UserWithRoles>)
);

export const selectUserRoles = (state: ApplicationState) =>
  state.user.profileMeta?.roles ?? [];

export const selectUserFeatureFlags = (state: ApplicationState) =>
  state.user.profileMeta?.featureFlags;

export const selectUserScopes = (state: ApplicationState) =>
  state.user.profileMeta?.scopes;

export const selectUserAccessTokenById = (state: ApplicationState) =>
  state.user.accessTokenById;

export const selectUserAccessTokenByUuid = (state: ApplicationState) =>
  state.user.accessTokenByUuid;

export const selectDeactivateUsers = (state: ApplicationState) =>
  state.user.deactivationRows;

export const selectEngineUser = (state: ApplicationState) =>
  state.user?.profileMeta?.engineUser;

export const selectUserCreateDrawerVisible = (state: ApplicationState) =>
  state.user.createDrawerVisible;

export const selectContactUsRequest = (state: ApplicationState) =>
  state.user.contactUsRequest;

export const selectPermissions = (state: ApplicationState) =>
  state.user.permissions;

export const selectHasMultipleUserExperiences = (state: ApplicationState) =>
  state.user.hasMultipleUserExperiences;

export const selectProfileMeta = (state: ApplicationState) =>
  state.user.profileMeta;

export const selectUserAccessByUser = (
  state: ApplicationState,
  userUuid: string
) => state.user.accessByUuid?.[userUuid];

export const selectAccountsGrantedToUser = createSelector(
  [selectUserAccessByUser, selectAccountsKeyedByUuid],
  (userAccess, accountsByUuid) =>
    userAccess?.grants.accounts?.map((uuid) => accountsByUuid[uuid]) ?? []
);

export const selectSubAccountsGrantedToUser = createSelector(
  [selectUserAccessByUser, selectAllSubAccountsKeyedByUuid],
  (userAccess, subAccountsByUuid) =>
    userAccess?.grants.subaccounts?.map((uuid) => subAccountsByUuid[uuid]) ?? []
);

export const selectSignedOnboardingContractsTypes = (
  state: ApplicationState
) => {
  const { contracts, signedContracts } = state.onboardingContract;

  return signedContracts.reduce((accum, { onboardingContractId }) => {
    const contractType = contracts.find(
      ({ id }) => onboardingContractId === id
    )?.type;

    if (contractType) {
      accum.push(contractType);
    }

    return accum;
  }, [] as OnboardingContractType[]);
};

/**
 * Create a getter to retrieve a user by an access token uuid.
 *
 * @param {ApplicationState} state
 * @return {GetUserByUuid}
 */
const makeGetUserByAccessTokenUuid = (state: ApplicationState): GetUserByUuid =>
  memoize(
    (accessTokenUuid: string = '') =>
      selectUserAccessTokenByUuid(state)?.[accessTokenUuid]?.richAccount?.user
  );

/**
 * Create a getter to retrieve a user's full name by an access token uuid.
 *
 * @param {ApplicationState} state
 * @return {GetUserFullNameByUuid}
 */
export const makeGetUserFullNameByAccessTokenUuid = (
  state: ApplicationState
): GetUserFullNameByUuid => {
  const getUserByAccessTokenUuid = makeGetUserByAccessTokenUuid(state);

  return memoize((accessTokenUuid: string = '') =>
    getUserFullName(getUserByAccessTokenUuid(accessTokenUuid))
  );
};

const mapUserToUserRow = (
  user: UserWithRoles | undefined,
  canSeeAllAccounts: boolean,
  accounts?: TempAccount[],
  accessByUuid?: { [uuid: string]: UserGrantsData },
  userDeactivation?: UserDeactivation
): UserRow | null => {
  if (!user) {
    return null;
  }

  const accountsUuids: string[] =
    (accessByUuid?.[user.uuid]?.grants?.accounts as string[]) || [];

  const userType = user?.roles ? getUserTypeRole(user.roles as Roles[]) : '';

  let userAccounts: UserAccountLink[];
  let accountsStatus: UserAccountsStatus;

  if (canSeeAllAccounts && accountsUuids.length === 0) {
    userAccounts = [{ name: 'All', uuid: '' }];
    accountsStatus = UserAccountsStatus.All;
  } else if (accountsUuids.length === 0) {
    userAccounts = [{ name: 'n/a', uuid: '' }];
    accountsStatus = UserAccountsStatus.Empty;
  } else {
    userAccounts = accountsUuids.map((uuid: string) => {
      const userAccount = accounts?.find((account) => account.uuid === uuid);

      return { name: userAccount?.name, uuid };
    });

    accountsStatus =
      (userAccounts[0].name as UserAccountsStatus) || UserAccountsStatus.Empty;
  }

  return {
    ...user,
    accessRoles: user?.roles ? getUserAccessRoles(user.roles as Roles[]) : [],
    accounts: userAccounts,
    accountsStatus,
    completeName: getUserFullName(user),
    deactivationId: userDeactivation?.id,
    isActive: !userDeactivation,
    isActiveDisplay: userDeactivation ? 'Inactive' : 'Active',
    partnerRoles: user?.roles ? getUserPartnerRoles(user.roles as Roles[]) : [],
    userType,
  };
};

export const userSelector = createSelector(
  [selectUsers, selectDeactivateUsers],
  (
    users: UserWithRoles[],
    userDeactivations: UserDeactivation[]
  ): UserWithDeactivationInfo[] => {
    return users.map((user: UserWithRoles) => ({
      ...user,
      isActive: !userDeactivations.find(
        (item) => item.userId === user.id && !('deletedBy' in item)
      ),
    }));
  }
);

// export const tableDataSelector = createSelector(
//   [selectUsers, selectAccounts, selectUsersAccess, selectDeactivateUsers],
//   (
//     users: User[],
//     accounts: Account[],
//     accessByUuid: any,
//     userDeactivations: UserDeactivation[]
//   ): UserRow[] => {
//     const deactivationIdsByUserId = groupBy(
//       userDeactivations?.filter((record) => !record.deletedAt),
//       'userId'
//     );

//     return users.map((user: User) =>
//       mapUserToUserRow(
//         user,
//         accounts,
//         accessByUuid,
//         deactivationIdsByUserId[user.id]?.[0]
//       )
//     );
//   }
// );

export const userDetailsSelector = createSelector(
  [
    ({ user }: ApplicationState, props: { uuid: string }) =>
      user.accessByUuid[props.uuid],
    ({ user }: ApplicationState, { uuid }: { uuid: string }) =>
      user.byUuid[uuid],
    selectDeactivateUsers,
    selectPermissions,
  ],
  (access, user, deactivateUsers, { canSeeAllAccounts }) => {
    const deactivateUser =
      user &&
      deactivateUsers.find(
        (item) => item.userId === user.id && !('deletedBy' in item)
      );
    const isActive = !deactivateUser;

    return {
      access,
      deactivationIds: deactivateUser?.id,
      isActive: isActive || false,
      user: mapUserToUserRow(user, canSeeAllAccounts),
    };
  }
);

export const mapUuidToName = (
  uuid: string | undefined,
  users: UserWithRoles[]
): string => {
  const userRecord = users.find((u) => u.uuid === uuid);

  return getUserFullName(userRecord);
};

export const mapAccountUuidToName = (
  users: UserWithRoles[],
  accountUuid?: string
): string => {
  const userRecord = users.find((u) => u.accountUuid === accountUuid);

  return getUserFullName(userRecord);
};

export const getUserNameByAccessTokenUuid = (user: UserState, uuid: string) => {
  const richToken = user.accessTokenByUuid[uuid];
  const userRecord = richToken?.richAccount.user;

  return getUserFullName(userRecord);
};

export const getAccountOwnerUuid = (
  accounts: TempAccount[],
  subAccount: PortalSubAccount,
  role: keyof Omit<AccountOwners, 'accountId'>
): string | undefined => {
  if (subAccount.subAccountOwners && subAccount.subAccountOwners[role]) {
    return subAccount.subAccountOwners[role] as string;
  }

  const account = accounts.find(
    (a: TempAccount) => a.uuid === subAccount.accountUuid
  );

  return account?.accountOwners
    ? (account.accountOwners[role] as string)
    : undefined;
};

// export const accountSelectors = createSelector(
//   [selectAccounts, ({ user }: ApplicationState) => user.featureFlags],
//   (accounts, featureFlags) => ({
//     accounts,
//     userProvisionedFeatureFlags: featureFlags,
//   })
// );

export const createAccountSelectors = createSelector(
  [
    ({ user }: ApplicationState) => user.accessByUuid[user.duplicateUuid],
    ({ user }: ApplicationState) => user.byUuid[user.duplicateUuid],
    selectPermissions,
  ],
  (access, user, { canSeeAllAccounts }) => ({
    access,
    formDefaults: mapUserToUserRow(user, canSeeAllAccounts),
  })
);

const selectUserAvatarProps = (
  _state: ApplicationState,
  props: UserAvatarProps
) => props;

export const userAvatarSelector = createSelector(
  [
    selectUserAccessTokenById,
    selectUserAccessTokenByUuid,
    selectUsers,
    selectUserAvatarProps,
  ],
  (accessTokenById, accessTokenByUuid, users, props) => {
    const { userUuid, accountUuid, accessTokenUuid, accessTokenId } = props;

    const hasIdentifier =
      userUuid || accountUuid || accessTokenUuid || accessTokenId;

    if (hasIdentifier) {
      const richAccessToken =
        accessTokenByUuid?.[accessTokenUuid || ''] ||
        accessTokenById?.[accessTokenId || ''];

      const richAccessTokenUserRecord = richAccessToken?.richAccount?.user;

      const userRecord =
        richAccessTokenUserRecord ||
        users.find(
          (user) => user.uuid === userUuid || user.accountUuid === accountUuid
        );

      if (userRecord) {
        return {
          name:
            `${userRecord.firstName || ''}${
              userRecord.lastName ? ` ${userRecord.lastName}` : ''
            }` || 'Unknown Name',
        };
      }
    }

    return {};
  }
);

export const selectIsOnboardingContractSigned = createSelector(
  [selectSignedOnboardingContractsTypes],
  (signedOnboardingContractsTypes) => {
    return (
      difference(
        [
          OnboardingContractType.TermsOfService,
          OnboardingContractType.PrivacyPolicy,
        ],
        signedOnboardingContractsTypes
      ).length === 0
    );
  }
);
